// TemplatesView.jsx — Custom Report Templates
// Hybrid system: gallery → create (excel upload OR canvas) → editor with
// live binding preview → one-click schedule.

import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Plus, Trash2, Edit2, Upload, FileSpreadsheet, LayoutGrid, Download,
  ArrowLeft, Play, Calendar, X, Type, Hash, Table as TableIcon,
  Minus, Image as ImageIcon, AlertCircle, Check, RefreshCw, Tag,
  Clock, Settings, Eye
} from 'lucide-react';
import reportTemplateService from '../services/reportTemplateService';
import authService from '../services/auth';
import mysqlService from '../services/mysql';
import questdbService from '../services/questdb';
import reportService from '../services/reportService';

const T = {
  ink:'#f3f4f6', text:'#e5e7eb', mute:'#9ca3af', faint:'#6b7280',
  bg:'#0a0e1a', soft:'#0d1526', line:'#1a2540', line2:'#27314a',
  surface:'#0f172a',
  accent:'#6366f1', accent2:'#a78bfa', accentSoft:'rgba(99,102,241,0.16)',
  danger:'#ef4444'
};

const s = {
  page:{ padding:'24px', height:'100%', overflowY:'auto', background:T.bg },
  header:{ display:'flex', alignItems:'center', gap:14, marginBottom:24 },
  title:{ fontSize:22, fontWeight:700, color:T.ink, letterSpacing:'-0.01em', margin:0 },
  subtle:{ fontSize:13, color:T.mute },
  btn:{ display:'inline-flex', alignItems:'center', gap:8, padding:'9px 16px', borderRadius:8,
        fontSize:14, fontWeight:600, border:`1px solid ${T.line}`, background:T.surface, color:T.ink, cursor:'pointer' },
  btnPrimary:{ background:T.accent, color:'#fff', borderColor:T.accent },
  btnDanger:{ background:T.surface, color:T.danger, borderColor:'#fecaca' },
  btnGhost:{ background:'transparent', borderColor:'transparent', color:T.mute }
};

// ───────── public component ───────────────────────────────────────────────
export default function TemplatesView({ defaultPlant = '' }) {
  const [view, setView] = useState('gallery');          // 'gallery' | 'editor'
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [createModal, setCreateModal] = useState(false);
  const [error, setError] = useState('');

  const refresh = useCallback(async () => {
    setLoading(true); setError('');
    try { setTemplates(await reportTemplateService.list()); }
    catch (e) { setError(e.message || 'Failed to load templates'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  if (view === 'editor' && selectedId) {
    return <TemplateEditor
      templateId={selectedId}
      onClose={() => { setSelectedId(null); setView('gallery'); refresh(); }}
      defaultPlant={defaultPlant}
    />;
  }

  return (
    <div style={s.page}>
      <div style={s.header}>
        <div>
          <h2 style={s.title}>Report templates</h2>
          <div style={s.subtle}>Build a report once, run it on a schedule.</div>
        </div>
        <div style={{ flex:1 }} />
        <button style={{ ...s.btn, ...s.btnPrimary }} onClick={() => setCreateModal(true)}>
          <Plus size={16} /> New template
        </button>
      </div>

      {error && <ErrorBanner msg={error} onClose={() => setError('')} />}

      {loading ? (
        <div style={{ padding:80, textAlign:'center', color:T.faint }}>
          <RefreshCw size={20} style={{ animation:'spin 1s linear infinite' }} />
        </div>
      ) : templates.length === 0 ? (
        <EmptyState onCreate={() => setCreateModal(true)} />
      ) : (
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(280px,1fr))', gap:16 }}>
          {templates.map(t => (
            <TemplateCard
              key={t.id}
              t={t}
              onOpen={() => { setSelectedId(t.id); setView('editor'); }}
              onDelete={async () => {
                if (!confirm(`Delete template "${t.name}"?`)) return;
                await reportTemplateService.remove(t.id);
                refresh();
              }}
            />
          ))}
        </div>
      )}

      {createModal && (
        <CreateTemplateModal
          defaultPlant={defaultPlant}
          onClose={() => setCreateModal(false)}
          onCreated={(tmpl) => { setCreateModal(false); setSelectedId(tmpl.id); setView('editor'); refresh(); }}
        />
      )}

      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}

// ───────── empty state ────────────────────────────────────────────────────
function EmptyState({ onCreate }) {
  return (
    <div style={{ padding:80, textAlign:'center', background:T.surface, border:`1px dashed ${T.line2}`, borderRadius:14 }}>
      <FileSpreadsheet size={36} color={T.faint} />
      <h3 style={{ margin:'14px 0 6px', color:T.ink, fontSize:18 }}>No templates yet</h3>
      <p style={{ color:T.mute, fontSize:14, marginBottom:18 }}>
        Upload an Excel you already use, or draw one in Miralys.
      </p>
      <button style={{ ...s.btn, ...s.btnPrimary }} onClick={onCreate}>
        <Plus size={16} /> Create your first template
      </button>
    </div>
  );
}

// ───────── gallery card ───────────────────────────────────────────────────
function TemplateCard({ t, onOpen, onDelete }) {
  const Icon = t.mode === 'excel' ? FileSpreadsheet : LayoutGrid;
  return (
    <div style={{
      background:T.surface, border:`1px solid ${T.line}`, borderRadius:14, padding:18,
      transition:'border-color .2s, transform .2s', cursor:'pointer'
    }}
      onMouseEnter={e => { e.currentTarget.style.borderColor = T.accent2; e.currentTarget.style.transform='translateY(-2px)'; }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = T.line; e.currentTarget.style.transform='translateY(0)'; }}
      onClick={onOpen}
    >
      <div style={{ display:'flex', alignItems:'center', gap:10 }}>
        <div style={{ width:38, height:38, borderRadius:9, background:T.accentSoft, color:T.accent2,
                      display:'flex', alignItems:'center', justifyContent:'center' }}>
          <Icon size={18} />
        </div>
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ fontWeight:600, color:T.ink, fontSize:15 }}>{t.name}</div>
          <div style={{ fontSize:12, color:T.faint, textTransform:'uppercase', letterSpacing:'0.06em' }}>
            {t.mode === 'excel' ? 'Excel template' : 'Canvas'}
          </div>
        </div>
        <button style={{ ...s.btn, ...s.btnGhost, padding:6 }} onClick={(e) => { e.stopPropagation(); onDelete(); }} title="Delete">
          <Trash2 size={14} />
        </button>
      </div>
      <div style={{ marginTop:12, paddingTop:12, borderTop:`1px solid ${T.line}`, fontSize:12, color:T.mute, display:'flex', gap:14 }}>
        <span><Tag size={11} style={{ verticalAlign:'middle' }} /> {t.bindingCount} bindings</span>
        {t.mode === 'canvas' && <span><LayoutGrid size={11} style={{ verticalAlign:'middle' }} /> {t.blockCount} blocks</span>}
        {t.mode === 'excel' && t.excelFile && <span><FileSpreadsheet size={11} style={{ verticalAlign:'middle' }} /> {(t.excelFile.size/1024).toFixed(0)} KB</span>}
      </div>
    </div>
  );
}

// ───────── create modal ───────────────────────────────────────────────────
function CreateTemplateModal({ onClose, onCreated, defaultPlant }) {
  const [step, setStep] = useState('mode');    // 'mode' | 'details'
  const [mode, setMode] = useState(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [plantId, setPlantId] = useState(defaultPlant);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const submit = async () => {
    setBusy(true); setErr('');
    try {
      let tmpl;
      if (mode === 'excel') {
        if (!file) { setErr('Pick an .xlsx file first'); setBusy(false); return; }
        tmpl = await reportTemplateService.createExcel({ name, description, plantId, file });
      } else {
        tmpl = await reportTemplateService.createCanvas({
          name, description, plantId,
          canvas: { pageSize:'A4', orientation:'portrait', width:794, height:1123, blocks:[] }
        });
      }
      onCreated(tmpl);
    } catch (e) {
      setErr(e.response?.data?.message || e.message || 'Failed to create template');
      setBusy(false);
    }
  };

  return (
    <Modal onClose={onClose} title={step === 'mode' ? 'Choose a starting point' : 'Template details'}>
      {step === 'mode' && (
        <div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14, marginBottom:18 }}>
            <ModeOption
              icon={<FileSpreadsheet size={26} />}
              title="Upload Excel"
              desc="Start from a .xlsx you already designed. Click cells to bind tags."
              recommended
              onClick={() => { setMode('excel'); setStep('details'); }}
            />
            <ModeOption
              icon={<LayoutGrid size={26} />}
              title="Draw in Miralys"
              desc="Drop text, KPIs, tables on a blank canvas. Best for screen-first reports."
              onClick={() => { setMode('canvas'); setStep('details'); }}
            />
          </div>
        </div>
      )}

      {step === 'details' && (
        <div>
          <Field label="Template name">
            <input style={inp()} placeholder="e.g. Daily Shift Report" value={name} onChange={e => setName(e.target.value)} autoFocus />
          </Field>
          <Field label="Description (optional)">
            <input style={inp()} placeholder="What does this report show?" value={description} onChange={e => setDescription(e.target.value)} />
          </Field>
          <Field label="Default plant (optional)">
            <input style={inp()} placeholder="e.g. plantA" value={plantId} onChange={e => setPlantId(e.target.value)} />
          </Field>
          {mode === 'excel' && (
            <Field label="Excel file (.xlsx)">
              <FileDropzone file={file} onPick={setFile} />
            </Field>
          )}
          {err && <ErrorBanner msg={err} onClose={() => setErr('')} />}
          {busy && (
            <div style={{ marginTop:14, marginBottom:6 }}>
              <div style={{
                position:'relative', height:6, borderRadius:3,
                background:T.line, overflow:'hidden'
              }}>
                <style>{`@keyframes indeterminate { 0% { left: -40%; } 100% { left: 100%; } }`}</style>
                <div style={{
                  position:'absolute', top:0, bottom:0, width:'40%',
                  background:`linear-gradient(90deg, transparent, ${T.accent2}, transparent)`,
                  animation:'indeterminate 1.2s ease-in-out infinite'
                }} />
              </div>
              <div style={{ display:'flex', alignItems:'center', gap:8, marginTop:8, color:T.mute, fontSize:12 }}>
                <Spinner size={11} />
                {mode === 'excel' && file
                  ? `Uploading ${file.name} (${(file.size/1024).toFixed(0)} KB) and extracting cells…`
                  : 'Creating template…'}
              </div>
            </div>
          )}
          <div style={{ display:'flex', justifyContent:'space-between', marginTop:18 }}>
            <button style={s.btn} onClick={() => setStep('mode')} disabled={busy}>Back</button>
            <button style={{ ...s.btn, ...s.btnPrimary, opacity: busy || !name ? 0.6 : 1 }}
              onClick={submit} disabled={busy || !name}>
              {busy ? 'Creating…' : 'Create template'}
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
}

function ModeOption({ icon, title, desc, recommended, onClick }) {
  return (
    <button onClick={onClick}
      style={{ textAlign:'left', padding:20, border:`1px solid ${T.line}`, borderRadius:12, background:T.surface,
               cursor:'pointer', transition:'all .2s' }}
      onMouseEnter={e => { e.currentTarget.style.borderColor = T.accent; e.currentTarget.style.background = T.accentSoft; }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = T.line; e.currentTarget.style.background = T.surface; }}
    >
      <div style={{ color:T.accent2, marginBottom:10 }}>{icon}</div>
      <div style={{ display:'flex', alignItems:'center', gap:8 }}>
        <strong style={{ color:T.ink, fontSize:15 }}>{title}</strong>
        {recommended && <span style={{ fontSize:10, background:T.accentSoft, color:T.accent2, padding:'2px 6px', borderRadius:4, fontWeight:600, letterSpacing:'0.05em' }}>RECOMMENDED</span>}
      </div>
      <p style={{ color:T.mute, fontSize:13, marginTop:6, lineHeight:1.5 }}>{desc}</p>
    </button>
  );
}

// ───────── editor (routes to excel or canvas) ─────────────────────────────
function TemplateEditor({ templateId, onClose, defaultPlant }) {
  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [scheduleModal, setScheduleModal] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [previewing, setPreviewing] = useState(false);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    reportTemplateService.get(templateId)
      .then(t => { if (mounted) setTemplate(t); })
      .catch(e => mounted && setErr(e.message))
      .finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, [templateId]);

  // Debounced save when template state changes
  const dirty = useRef(false);
  const lastSaved = useRef(null);
  useEffect(() => {
    if (!template) return;
    if (!dirty.current) { dirty.current = true; return; }    // skip initial set
    const handle = setTimeout(async () => {
      try {
        setSaving(true);
        await reportTemplateService.update(template.id, {
          name: template.name,
          description: template.description,
          plantId: template.plantId,
          canvas: template.canvas,
          bindings: template.bindings
        });
        setSavedAt(new Date());
        lastSaved.current = Date.now();
      } catch (e) { setErr(e.message); }
      finally { setSaving(false); }
    }, 700);
    return () => clearTimeout(handle);
  }, [template]);

  const updateBindings = (next) => setTemplate(t => ({ ...t, bindings: next }));
  const updateCanvas = (next) => setTemplate(t => ({ ...t, canvas: next }));

  const runPreview = async () => {
    setPreviewing(true);
    try {
      const data = await reportTemplateService.previewBindings(template.id);
      setPreviewData(data.resolved);
    } catch (e) { setErr(e.message); }
    finally { setPreviewing(false); }
  };

  const downloadGenerated = async () => {
    try {
      const blob = await reportTemplateService.generate(template.id, { save: false });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `${template.name || 'report'}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { setErr(e.message); }
  };

  if (loading) {
    // Render the editor chrome shape so there's no layout jump when the real
    // editor mounts — toolbar at top, skeleton spreadsheet below, no right rail.
    return (
      <div style={{ display:'flex', flexDirection:'column', height:'100%', background:T.bg }}>
        <div style={{
          display:'flex', alignItems:'center', gap:12,
          padding:'14px 22px', background:T.surface, borderBottom:`1px solid ${T.line}`
        }}>
          <button style={{ ...s.btn, ...s.btnGhost, padding:6 }} onClick={onClose}><ArrowLeft size={16} /></button>
          <div style={{
            height:18, width:200, borderRadius:4,
            background:`linear-gradient(90deg, ${T.line} 0%, ${T.line2} 50%, ${T.line} 100%)`,
            backgroundSize:'400px 100%',
            animation:'shimmer 1.6s linear infinite'
          }} />
          <span style={{ flex:1 }} />
          <Spinner size={14} />
          <span style={{ fontSize:12, color:T.faint }}>Loading template…</span>
        </div>
        <div style={{ flex:1, padding:18, overflow:'auto' }}>
          <SheetSkeleton message="Loading template…" />
        </div>
      </div>
    );
  }
  if (!template) return <div style={{ padding:24, color:T.danger }}>Template not found.</div>;

  return (
    <div style={{ display:'flex', flexDirection:'column', height:'100%', background:T.bg }}>
      {/* Editor toolbar */}
      <div style={{ display:'flex', alignItems:'center', gap:12, padding:'14px 22px', background:T.surface, borderBottom:`1px solid ${T.line}` }}>
        <button style={{ ...s.btn, ...s.btnGhost, padding:6 }} onClick={onClose}><ArrowLeft size={16} /></button>
        <input
          value={template.name}
          onChange={e => setTemplate({ ...template, name: e.target.value })}
          style={{ border:'none', background:'transparent', fontSize:18, fontWeight:700, color:T.ink, outline:'none', flex:1, minWidth:0 }}
        />
        <span style={{ fontSize:12, color:T.faint }}>
          {saving ? 'Saving…' : savedAt ? `Saved ${timeAgo(savedAt)}` : 'All changes saved'}
        </span>
        <div style={{ width:1, height:24, background:T.line }} />
        <button style={s.btn} onClick={runPreview} disabled={previewing}>
          <Eye size={14} /> {previewing ? 'Previewing…' : 'Preview values'}
        </button>
        <button style={s.btn} onClick={downloadGenerated}>
          <Download size={14} /> Generate now
        </button>
        <button style={{ ...s.btn, ...s.btnPrimary }} onClick={() => setScheduleModal(true)}>
          <Calendar size={14} /> Schedule
        </button>
      </div>

      {err && <ErrorBanner msg={err} onClose={() => setErr('')} />}

      <div style={{ flex:1, display:'flex', minHeight:0 }}>
        <div style={{ flex:1, overflow:'auto', minWidth:0 }}>
          {template.mode === 'excel'
            ? <ExcelEditor template={template} previewData={previewData} updateBindings={updateBindings} />
            : <CanvasEditor template={template} previewData={previewData} updateCanvas={updateCanvas} updateBindings={updateBindings} />
          }
        </div>
      </div>

      {scheduleModal && (
        <ScheduleTemplateModal
          template={template}
          onClose={() => setScheduleModal(false)}
          onScheduled={() => { setScheduleModal(false); }}
        />
      )}
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}

// ───────── Excel-mode editor ──────────────────────────────────────────────
function ExcelEditor({ template, previewData, updateBindings }) {
  const [sheets, setSheets] = useState([]);
  const [activeSheet, setActiveSheet] = useState('');
  const [selectedRange, setSelectedRange] = useState(null);        // { start:'A2', end:'A121' }
  const [selectedBindingId, setSelectedBindingId] = useState(null);
  const [err, setErr] = useState('');
  // initialLoading=true on first fetch — distinguishes "still loading" from
  // "loaded, but empty" so we don't flash a misleading "no preview" message.
  const [initialLoading, setInitialLoading] = useState(true);
  // Extend the visible area past the source file's used range so the user
  // can click into empty cells. Bumps a fixed amount each click.
  const [padRows, setPadRows] = useState(30);
  const [padCols, setPadCols] = useState(10);
  const [loadingMore, setLoadingMore] = useState(false);
  const dragRef = useRef({ active:false, anchor:null });

  useEffect(() => {
    let mounted = true;
    setLoadingMore(true);
    reportTemplateService.cellPreview(template.id, undefined, { padRows, padCols })
      .then(sh => {
        if (!mounted) return;
        setSheets(sh);
        if (sh.length && !activeSheet) setActiveSheet(sh[0].name);
      })
      .catch(e => mounted && setErr(e.message))
      .finally(() => {
        if (!mounted) return;
        setLoadingMore(false);
        setInitialLoading(false);
      });
    return () => { mounted = false; };
  }, [template.id, padRows, padCols]);

  // Cleanly end any drag if the user releases outside the grid
  useEffect(() => {
    const up = () => { dragRef.current.active = false; };
    document.addEventListener('mouseup', up);
    return () => document.removeEventListener('mouseup', up);
  }, []);

  const sheet = sheets.find(s => s.name === activeSheet);
  const bindingsForSheet = template.bindings.filter(b => b.target?.sheet === activeSheet || !b.target?.sheet);
  const bindingByCell = useMemo(() => {
    const map = new Map();
    bindingsForSheet.forEach(b => {
      if (b.target?.cell) map.set(b.target.cell, b);
      if (b.target?.range) {
        const m = b.target.range.match(/^([A-Z]+)(\d+):([A-Z]+)(\d+)$/);
        if (m) {
          const c1 = colToIdx(m[1]), c2 = colToIdx(m[3]);
          const r1 = +m[2], r2 = +m[4];
          for (let c=c1; c<=c2; c++) for (let r=r1; r<=r2; r++) {
            map.set(`${idxToCol(c)}${r}`, b);
          }
        }
      }
    });
    return map;
  }, [bindingsForSheet]);

  // Compute the canonical range string from the current selection
  const rangeInfo = useMemo(() => {
    if (!selectedRange) return null;
    const a = parseAddr(selectedRange.start), b = parseAddr(selectedRange.end);
    if (!a || !b) return null;
    const c1 = Math.min(a.colIdx, b.colIdx), c2 = Math.max(a.colIdx, b.colIdx);
    const r1 = Math.min(a.row, b.row),       r2 = Math.max(a.row, b.row);
    const startAddr = `${idxToCol(c1)}${r1}`;
    const endAddr   = `${idxToCol(c2)}${r2}`;
    const isSingle  = startAddr === endAddr;
    const slotCount = (c2 - c1 + 1) * (r2 - r1 + 1);
    return { c1, c2, r1, r2, startAddr, endAddr, isSingle, slotCount, rangeStr: isSingle ? startAddr : `${startAddr}:${endAddr}` };
  }, [selectedRange]);

  const onCellMouseDown = (addr) => {
    dragRef.current = { active:true, anchor:addr };
    setSelectedRange({ start:addr, end:addr });
    const existing = bindingByCell.get(addr);
    setSelectedBindingId(existing ? existing.id : null);
  };
  const onCellMouseEnter = (addr) => {
    if (!dragRef.current.active) return;
    setSelectedRange({ start:dragRef.current.anchor, end:addr });
  };

  const handleBindClick = () => {
    if (!rangeInfo) return;
    const isSingle = rangeInfo.isSingle;
    const sameRow  = rangeInfo.r1 === rangeInfo.r2;
    const target = isSingle
      ? { sheet:activeSheet, cell:rangeInfo.startAddr }
      : { sheet:activeSheet, range:rangeInfo.rangeStr };

    const newB = makeBlankBinding({
      label: isSingle ? `Binding at ${rangeInfo.startAddr}` : `Range ${rangeInfo.rangeStr}`,
      target,
      plantId: template.plantId
    });
    if (!isSingle) {
      newB.source.timeRule.mode = 'expand';
      newB.source.timeRule.expandInto = sameRow ? 'column' : 'row';
    }
    updateBindings([...template.bindings, newB]);
    setSelectedBindingId(newB.id);
  };

  const onBindingChange = (next) => {
    updateBindings(template.bindings.map(b => b.id === next.id ? next : b));
  };
  const removeBinding = (id) => {
    updateBindings(template.bindings.filter(b => b.id !== id));
    if (selectedBindingId === id) setSelectedBindingId(null);
  };

  const selectedBinding = template.bindings.find(b => b.id === selectedBindingId);

  return (
    <div style={{ display:'flex', height:'100%' }}>
      {/* main: sheet preview */}
      <div style={{ flex:1, overflow:'auto', padding:18, minWidth:0 }}>
        {err && <ErrorBanner msg={err} onClose={() => setErr('')} />}
        <div style={{ display:'flex', gap:6, marginBottom:12, alignItems:'center', flexWrap:'wrap' }}>
          {sheets.map(sh => (
            <button key={sh.name}
              onClick={() => setActiveSheet(sh.name)}
              style={{ ...s.btn, ...(sh.name === activeSheet ? { borderColor: T.accent2, color: T.accent2, background: T.accentSoft } : {}) }}>
              {sh.name}
            </button>
          ))}
          <span style={{ flex:1 }} />
          {sheet && (
            <span style={{ fontSize:11, color:T.faint, marginRight:8 }}>
              {sheet.rowCount} rows × {sheet.columnCount} cols
            </span>
          )}
          <button
            style={{ ...s.btn, padding:'5px 10px', fontSize:12 }}
            onClick={() => setPadRows(r => r + 50)}
            disabled={loadingMore}
            title="Show 50 more empty rows so you can bind into them"
          >+ 50 rows</button>
          <button
            style={{ ...s.btn, padding:'5px 10px', fontSize:12 }}
            onClick={() => setPadCols(c => c + 10)}
            disabled={loadingMore}
            title="Show 10 more empty columns"
          >+ 10 cols</button>
        </div>
        {initialLoading ? (
          <SheetSkeleton message="Loading template…" />
        ) : sheet ? (
          <div style={{ position:'relative' }}>
            <SheetGrid
              sheet={sheet}
              rangeInfo={rangeInfo}
              bindingByCell={bindingByCell}
              previewData={previewData}
              onCellMouseDown={onCellMouseDown}
              onCellMouseEnter={onCellMouseEnter}
            />
            {loadingMore && (
              <div style={{
                position:'absolute', top:8, right:8,
                padding:'5px 10px', borderRadius:6,
                background:'rgba(15,23,42,0.92)', color:T.text,
                fontSize:11, display:'flex', alignItems:'center', gap:6,
                border:`1px solid ${T.line2}`, backdropFilter:'blur(4px)'
              }}>
                <Spinner size={11} /> Expanding grid…
              </div>
            )}
          </div>
        ) : (
          <div style={{ color:T.faint, padding:30 }}>No sheets in this template.</div>
        )}
      </div>

      {/* side: bindings panel */}
      <aside style={{ width:380, flexShrink:0, borderLeft:`1px solid ${T.line}`, background:T.surface, padding:'14px 16px', overflowY:'auto', overflowX:'hidden', minWidth:0 }}>
        <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:8 }}>
          <span style={{ fontSize:11, color:T.faint, fontWeight:600, textTransform:'uppercase', letterSpacing:'0.08em' }}>
            Bindings · {template.bindings?.length || 0}
          </span>
          <div style={{ flex:1 }} />
          <button
            style={{
              ...s.btn,
              padding:'5px 12px', fontSize:11.5,
              ...(rangeInfo ? s.btnPrimary : { opacity:0.4, cursor:'not-allowed' })
            }}
            onClick={handleBindClick}
            disabled={!rangeInfo}
            title={rangeInfo && !rangeInfo.isSingle ? `Creates one binding for ${rangeInfo.slotCount} cells (mode = expand)` : ''}
          >
            <Plus size={11} /> {rangeInfo ? `Bind ${rangeInfo.rangeStr}` : 'Select a cell to bind'}
          </button>
        </div>

        <BindingsList
          bindings={template.bindings}
          selectedId={selectedBindingId}
          previewData={previewData}
          onSelect={setSelectedBindingId}
          onRemove={removeBinding}
        />

        {selectedBinding && (
          <BindingPanel
            binding={selectedBinding}
            onChange={onBindingChange}
            defaultPlant={template.plantId}
          />
        )}

        {!rangeInfo && !selectedBinding && (
          <div style={{ padding:24, textAlign:'center', color:T.faint, fontSize:13, background:T.bg, borderRadius:10, border:`1px dashed ${T.line2}`, marginTop:12 }}>
            Click a cell, or click-drag to select a range, then hit <strong style={{ color:T.text }}>Bind</strong>.
          </div>
        )}
      </aside>
    </div>
  );
}

// Inside the sheet preview, we render against Excel's natural white-paper
// palette — NOT the app's dark theme. Otherwise an Excel cell with black
// text on white (the default) would render as black text on slate (invisible).
const EXCEL_THEME = {
  paper:        '#FFFFFF',   // cell default background
  paperText:    '#000000',   // cell default text
  gridLine:     '#D4D4D4',   // gridlines for cells without explicit borders
  headerBg:     '#F2F2F2',   // row/col header strip
  headerText:   '#595959',
  headerBorder: '#BFBFBF',
  selection:    '#217346',   // Excel green for selection ring
  selectionFill:'rgba(33,115,70,0.10)',
  bindingMark:  '#7C3AED'    // purple corner triangle for bound cells
};

// Inline animated spinner — uses an injected style block since inline styles
// can't define keyframes.
function Spinner({ size = 14, color = T.accent2 }) {
  return (
    <>
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
      <span style={{
        display:'inline-block', width:size, height:size,
        border:`2px solid ${color}33`, borderTopColor:color,
        borderRadius:'50%', animation:'spin 0.8s linear infinite'
      }} />
    </>
  );
}

// Skeleton placeholder while the cell grid is loading — looks like an empty
// spreadsheet with a centered loading indicator. Renders enough rows to fill
// the typical viewport so we don't leave a big dark band below the table.
function SheetSkeleton({ message = 'Loading…' }) {
  const rows = 40, cols = 12;
  return (
    <div style={{
      position:'relative',
      background: EXCEL_THEME.paper,
      border:`1px solid ${T.line}`,
      borderRadius:10,
      overflow:'hidden',
      colorScheme:'light',
      // Fill the available vertical space so the white paper covers the editor
      // area instead of leaving the dark page background visible below.
      minHeight:'70vh',
      display:'flex', flexDirection:'column'
    }}>
      <style>{`@keyframes shimmer { 0% { background-position: -800px 0; } 100% { background-position: 800px 0; } }`}</style>
      <div style={{ flex:1, overflow:'hidden', background: EXCEL_THEME.paper }}>
        <table style={{ borderCollapse:'separate', borderSpacing:0, width:'100%', tableLayout:'fixed' }}>
          <thead>
            <tr>
              <th style={hCell({ width:40 })}></th>
              {Array.from({ length: cols }, (_, ci) => (
                <th key={ci} style={hCell({ width:90 })}>{idxToCol(ci+1)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: rows }, (_, ri) => (
              <tr key={ri}>
                <td style={hCell({ width:40, textAlign:'center' })}>{ri+1}</td>
                {Array.from({ length: cols }, (_, ci) => (
                  <td key={ci} style={{
                    padding:'5px 10px',
                    borderRight:`1px solid ${EXCEL_THEME.gridLine}`,
                    borderBottom:`1px solid ${EXCEL_THEME.gridLine}`,
                    height:26,
                    background:'linear-gradient(90deg, #FFFFFF 0%, #F0F0F0 50%, #FFFFFF 100%)',
                    backgroundSize:'800px 100%',
                    animation:`shimmer 1.6s linear ${(ri * 0.04).toFixed(2)}s infinite`
                  }} />
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div style={{
        position:'absolute', inset:0,
        display:'flex', alignItems:'center', justifyContent:'center',
        background:'rgba(255,255,255,0.55)', pointerEvents:'none'
      }}>
        <div style={{
          display:'flex', alignItems:'center', gap:10,
          padding:'10px 16px', borderRadius:8,
          background:'#0f172a', color:T.text,
          fontSize:13, fontWeight:500,
          boxShadow:'0 6px 24px rgba(0,0,0,0.25)'
        }}>
          <Spinner size={14} color={T.accent2} />
          {message}
        </div>
      </div>
    </div>
  );
}

function SheetGrid({ sheet, rangeInfo, bindingByCell, previewData, onCellMouseDown, onCellMouseEnter }) {
  const cols = sheet.columnCount || 0;
  const rows = sheet.rowCount || 0;
  if (!cols || !rows) return <div style={{ color:T.faint }}>Empty sheet.</div>;

  const inRange = (c, r) => rangeInfo && c >= rangeInfo.c1 && c <= rangeInfo.c2 && r >= rangeInfo.r1 && r <= rangeInfo.r2;
  const isAnchor = (addr) => rangeInfo && rangeInfo.startAddr === addr;

  // Build a "covered by merge" map and a "merge anchor → {colspan, rowspan}" map
  const coverage = useMemo(() => {
    const covered = new Set();
    const anchors = new Map();
    for (const range of sheet.merges || []) {
      const m = range.match(/^([A-Z]+)(\d+):([A-Z]+)(\d+)$/i);
      if (!m) continue;
      const c1 = colToIdx(m[1]), r1 = +m[2], c2 = colToIdx(m[3]), r2 = +m[4];
      const anchor = `${idxToCol(c1)}${r1}`;
      anchors.set(anchor, { colspan: c2 - c1 + 1, rowspan: r2 - r1 + 1 });
      for (let r = r1; r <= r2; r++) for (let c = c1; c <= c2; c++) {
        if (r === r1 && c === c1) continue;
        covered.add(`${idxToCol(c)}${r}`);
      }
    }
    return { covered, anchors };
  }, [sheet]);

  const colWidths = sheet.colWidths || [];
  const rowHeights = sheet.rowHeights || [];

  return (
    <div style={{
      overflow:'auto',
      background: EXCEL_THEME.paper,
      border:`1px solid ${T.line}`,
      borderRadius:10,
      // Hint the browser that this region is light so form controls / scrollbars
      // render appropriately even when the app is dark.
      colorScheme: 'light'
    }}>
      <table style={{ borderCollapse:'separate', borderSpacing:0, fontSize:12, fontFamily:'Calibri, "Segoe UI", system-ui, sans-serif', background: EXCEL_THEME.paper }}>
        <thead>
          <tr>
            <th style={hCell({ width:40 })}></th>
            {Array.from({ length: cols }, (_, ci) => (
              <th key={ci} style={hCell({ width: colWidths[ci] || 80 })}>{idxToCol(ci+1)}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: rows }, (_, ri) => (
            <tr key={ri} style={rowHeights[ri] ? { height: rowHeights[ri] } : undefined}>
              <td style={hCell({ width:40, textAlign:'center' })}>{ri+1}</td>
              {Array.from({ length: cols }, (_, ci) => {
                const addr = `${idxToCol(ci+1)}${ri+1}`;
                if (coverage.covered.has(addr)) return null;
                const value = sheet.cells?.[ri]?.[ci];
                const style = sheet.styles?.[ri]?.[ci];
                const binding = bindingByCell.get(addr);
                const inSel = inRange(ci+1, ri+1);
                const isAnchorCell = isAnchor(addr);
                const resolved = binding && previewData?.[binding.id];
                const overlayValue = resolved && resolved.kind === 'scalar' ? resolved.value : null;
                const merge = coverage.anchors.get(addr);
                return (
                  <td
                    key={ci}
                    colSpan={merge?.colspan}
                    rowSpan={merge?.rowspan}
                    onMouseDown={(e) => { e.preventDefault(); onCellMouseDown?.(addr); }}
                    onMouseEnter={() => onCellMouseEnter?.(addr)}
                    style={cellStyle({ style, isSelected: isAnchorCell, inRange: inSel, hasBinding: !!binding, width: colWidths[ci] || 80 })}
                    title={binding ? `Bound: ${binding.label}` : addr}
                  >
                    {/* Bound-cell corner badge — doesn't mask the original background */}
                    {binding && (
                      <span style={{
                        position:'absolute', top:0, right:0,
                        width:0, height:0,
                        borderTop:`8px solid ${EXCEL_THEME.bindingMark}`,
                        borderLeft:`8px solid transparent`
                      }} />
                    )}
                    {overlayValue !== null && overlayValue !== undefined
                      ? <span style={{ color:T.accent2, fontWeight:600 }}>{String(overlayValue)}</span>
                      : value !== null && value !== undefined ? String(value) : ''}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const hCell = (extra = {}) => ({
  padding: '4px 8px',
  background: EXCEL_THEME.headerBg,
  borderRight: `1px solid ${EXCEL_THEME.headerBorder}`,
  borderBottom: `1px solid ${EXCEL_THEME.headerBorder}`,
  color: EXCEL_THEME.headerText,
  fontSize: 11,
  fontWeight: 500,
  textAlign: 'center',
  position: 'sticky',
  top: 0,
  zIndex: 1,
  ...extra
});

function cellStyle({ style, isSelected, inRange, hasBinding, width }) {
  // Build per-side borders from extracted cell.border, falling back to a
  // subtle gridline. We render against Excel paper (white), so the default
  // gridline matches Excel's gray, not the app's dark slate.
  const fallback = `1px solid ${EXCEL_THEME.gridLine}`;
  const bd = style?.bd || {};
  const borderTop    = bd.t || fallback;
  const borderRight  = bd.r || fallback;
  const borderBottom = bd.b || fallback;
  const borderLeft   = bd.l || fallback;

  // Original Excel cell colors win; default = white paper + black text.
  const bg = style?.bg || EXCEL_THEME.paper;
  const fg = style?.fg || EXCEL_THEME.paperText;

  const base = {
    padding: style?.wrap ? '4px 8px' : '5px 10px',
    borderTop, borderRight, borderBottom, borderLeft,
    color: fg,
    background: bg,
    minWidth: 60,
    width,
    whiteSpace: style?.wrap ? 'normal' : 'nowrap',
    wordBreak: style?.wrap ? 'break-word' : 'normal',
    overflow: 'hidden',
    textOverflow: style?.wrap ? 'clip' : 'ellipsis',
    cursor: 'cell',
    userSelect: 'none',
    position: 'relative',
    fontFamily: style?.ff || 'Calibri, "Segoe UI", system-ui, sans-serif',
    fontWeight: style?.b ? 700 : 400,
    fontStyle: style?.i ? 'italic' : 'normal',
    textDecoration: style?.u ? 'underline' : 'none',
    // Cap displayed font at 16 so a 36pt header doesn't blow up the row
    fontSize: style?.fs ? Math.min(Math.round(style.fs), 16) : 12,
    textAlign: style?.ha || 'left',
    verticalAlign: style?.va || 'middle'
  };
  if (isSelected) {
    base.boxShadow = `inset 0 0 0 2px ${EXCEL_THEME.selection}`;
  } else if (inRange) {
    base.boxShadow = `inset 0 0 0 1px ${EXCEL_THEME.selection}, inset 0 0 0 9999px ${EXCEL_THEME.selectionFill}`;
  } else if (hasBinding) {
    // Original cell color preserved — marker is the corner triangle in SheetGrid
    base.boxShadow = `inset 0 0 0 1px rgba(124,58,237,0.45)`;
  }
  return base;
}

// ───────── Canvas-mode editor ─────────────────────────────────────────────
function CanvasEditor({ template, previewData, updateCanvas, updateBindings }) {
  const [selectedBlockId, setSelectedBlockId] = useState(null);
  const canvas = template.canvas || { blocks:[], width:794, height:1123 };
  const canvasRef = useRef(null);
  const dragRef = useRef(null);

  const addBlock = (type) => {
    const id = uid();
    const defaults = {
      text:    { type:'text',    w:300, h:36, content:{ text:'New text block' } },
      kpi:     { type:'kpi',     w:200, h:100, content:{ label:'KPI label' } },
      table:   { type:'table',   w:500, h:200, content:{} },
      divider: { type:'divider', w:500, h:8, content:{} },
      chart:   { type:'chart',   w:400, h:240, content:{} }
    };
    const tpl = defaults[type] || defaults.text;
    const block = { id, ...tpl, x:40, y:40, style:{}, bindingId:'' };
    updateCanvas({ ...canvas, blocks: [...(canvas.blocks||[]), block] });
    setSelectedBlockId(id);
  };

  const updateBlock = (id, patch) => {
    updateCanvas({ ...canvas, blocks: canvas.blocks.map(b => b.id === id ? { ...b, ...patch } : b) });
  };
  const removeBlock = (id) => {
    updateCanvas({ ...canvas, blocks: canvas.blocks.filter(b => b.id !== id) });
    if (selectedBlockId === id) setSelectedBlockId(null);
    // remove associated binding too
    const block = canvas.blocks.find(b => b.id === id);
    if (block?.bindingId) updateBindings(template.bindings.filter(bd => bd.id !== block.bindingId));
  };

  // Drag handling — mousedown on a block, then track on document
  const startDrag = (e, blockId, mode) => {
    e.preventDefault(); e.stopPropagation();
    const block = canvas.blocks.find(b => b.id === blockId);
    if (!block) return;
    dragRef.current = {
      blockId, mode, startX:e.clientX, startY:e.clientY,
      origX:block.x, origY:block.y, origW:block.w, origH:block.h
    };
    setSelectedBlockId(blockId);
    document.addEventListener('mousemove', onDrag);
    document.addEventListener('mouseup', endDrag);
  };
  const onDrag = (e) => {
    const d = dragRef.current; if (!d) return;
    const dx = e.clientX - d.startX, dy = e.clientY - d.startY;
    if (d.mode === 'move') updateBlock(d.blockId, { x: Math.max(0, d.origX + dx), y: Math.max(0, d.origY + dy) });
    else if (d.mode === 'resize') updateBlock(d.blockId, { w: Math.max(40, d.origW + dx), h: Math.max(20, d.origH + dy) });
  };
  const endDrag = () => {
    dragRef.current = null;
    document.removeEventListener('mousemove', onDrag);
    document.removeEventListener('mouseup', endDrag);
  };

  const selectedBlock = canvas.blocks.find(b => b.id === selectedBlockId);
  const selectedBinding = selectedBlock?.bindingId
    ? template.bindings.find(b => b.id === selectedBlock.bindingId)
    : null;

  const attachBinding = () => {
    if (!selectedBlock) return;
    const newB = makeBlankBinding({
      label: selectedBlock.content?.label || selectedBlock.type,
      target: { blockId: selectedBlock.id },
      plantId: template.plantId
    });
    updateBindings([...template.bindings, newB]);
    updateBlock(selectedBlock.id, { bindingId: newB.id });
  };
  const onBindingChange = (next) => updateBindings(template.bindings.map(b => b.id === next.id ? next : b));

  return (
    <div style={{ display:'flex', height:'100%' }}>
      {/* Tool palette */}
      <div style={{ width:64, borderRight:`1px solid ${T.line}`, background:T.surface, padding:'12px 0', display:'flex', flexDirection:'column', alignItems:'center', gap:6 }}>
        {[
          { type:'text', Icon:Type, label:'Text' },
          { type:'kpi', Icon:Hash, label:'KPI' },
          { type:'table', Icon:TableIcon, label:'Table' },
          { type:'divider', Icon:Minus, label:'Line' },
        ].map(({type, Icon, label}) => (
          <button key={type}
            title={label}
            onClick={() => addBlock(type)}
            style={{ width:48, height:48, border:`1px solid ${T.line}`, borderRadius:8, background:T.surface, cursor:'pointer',
                     display:'flex', alignItems:'center', justifyContent:'center', color:T.mute }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = T.accent; e.currentTarget.style.color = T.accent2; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = T.line; e.currentTarget.style.color = T.mute; }}>
            <Icon size={18} />
          </button>
        ))}
      </div>

      {/* Canvas */}
      <div style={{ flex:1, overflow:'auto', padding:30, background:T.bg, minWidth:0 }} onClick={() => setSelectedBlockId(null)}>
        <div
          ref={canvasRef}
          style={{ width:canvas.width, height:canvas.height, background:'#fff', boxShadow:'0 4px 20px rgba(11,18,32,0.08)', position:'relative', margin:'0 auto', border:`1px solid ${T.line}` }}
          onClick={(e) => e.stopPropagation()}
        >
          {(canvas.blocks || []).map(block => {
            const isSel = block.id === selectedBlockId;
            const resolved = block.bindingId && previewData?.[block.bindingId];
            return (
              <div
                key={block.id}
                onMouseDown={e => startDrag(e, block.id, 'move')}
                onClick={(e) => { e.stopPropagation(); setSelectedBlockId(block.id); }}
                style={{ position:'absolute', left:block.x, top:block.y, width:block.w, height:block.h,
                         border:`1px ${isSel ? 'solid' : 'dashed'} ${isSel ? T.accent : T.line2}`,
                         borderRadius:6, background:'#fff', padding:8,
                         boxSizing:'border-box', userSelect:'none', cursor: isSel ? 'move' : 'pointer',
                         display:'flex', alignItems:'center', justifyContent:block.type==='kpi'?'flex-start':'center',
                         flexDirection: block.type==='kpi'? 'column':'row',
                         textAlign:'center'
                       }}>
                <BlockRender block={block} resolved={resolved} />
                {isSel && (
                  <span
                    onMouseDown={e => startDrag(e, block.id, 'resize')}
                    style={{ position:'absolute', right:-6, bottom:-6, width:14, height:14, borderRadius:3, background:T.accent, border:'2px solid #fff', cursor:'nwse-resize' }}
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Side: block / binding panel */}
      <aside style={{ width:380, flexShrink:0, borderLeft:`1px solid ${T.line}`, background:T.surface, padding:18, overflowY:'auto', overflowX:'hidden', minWidth:0 }}>
        {!selectedBlock ? (
          <div style={{ padding:24, textAlign:'center', color:T.faint, fontSize:13, background:T.bg, borderRadius:10, border:`1px dashed ${T.line2}` }}>
            Add a block from the left, then click it to edit its content and bind data.
          </div>
        ) : (
          <>
            <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:14 }}>
              <LayoutGrid size={16} color={T.accent2} />
              <strong style={{ color:T.ink, fontSize:14, textTransform:'capitalize' }}>{selectedBlock.type} block</strong>
              <div style={{ flex:1 }} />
              <button style={{ ...s.btn, ...s.btnDanger, padding:'5px 9px', fontSize:12 }} onClick={() => removeBlock(selectedBlock.id)}>
                <Trash2 size={12} />
              </button>
            </div>

            {selectedBlock.type === 'text' && (
              <Field label="Text content">
                <textarea style={{ ...inp(), height:90 }} value={selectedBlock.content?.text || ''}
                  onChange={e => updateBlock(selectedBlock.id, { content:{ ...selectedBlock.content, text:e.target.value } })} />
              </Field>
            )}
            {selectedBlock.type === 'kpi' && (
              <Field label="KPI label">
                <input style={inp()} value={selectedBlock.content?.label || ''}
                  onChange={e => updateBlock(selectedBlock.id, { content:{ ...selectedBlock.content, label:e.target.value } })} />
              </Field>
            )}

            <div style={{ marginTop:14, borderTop:`1px solid ${T.line}`, paddingTop:14 }}>
              <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:10 }}>
                <Tag size={14} color={T.accent2} />
                <strong style={{ color:T.ink, fontSize:13 }}>Data binding</strong>
                {!selectedBinding && <button style={{ ...s.btn, ...s.btnPrimary, padding:'4px 10px', fontSize:12, marginLeft:'auto' }} onClick={attachBinding}><Plus size={12} /> Bind</button>}
              </div>
              {selectedBinding && <BindingPanel binding={selectedBinding} onChange={onBindingChange} defaultPlant={template.plantId} embedded />}
            </div>
          </>
        )}
      </aside>
    </div>
  );
}

// Canvas blocks render onto a white "paper" surface, so block contents
// must use dark text/colors regardless of the surrounding (dark) theme.
const PAPER = { ink:'#0b1220', text:'#1f2937', mute:'#6b7280', faint:'#9ca3af', line:'#e5e7eb' };

function BlockRender({ block, resolved }) {
  if (block.type === 'text') {
    return <span style={{ color:PAPER.ink, fontSize:14 }}>{block.content?.text || 'Text'}</span>;
  }
  if (block.type === 'divider') {
    return <div style={{ width:'100%', height:1, background:PAPER.line }} />;
  }
  if (block.type === 'kpi') {
    const val = resolved?.value ?? resolved?.raw;
    return (
      <div style={{ textAlign:'center', width:'100%' }}>
        <div style={{ fontSize:11, color:PAPER.mute, textTransform:'uppercase', letterSpacing:'0.08em', fontWeight:600 }}>
          {block.content?.label || 'KPI'}
        </div>
        <div style={{ fontSize:28, fontWeight:700, color:PAPER.ink, marginTop:4 }}>
          {val !== undefined && val !== null ? String(val) : '—'}
        </div>
      </div>
    );
  }
  if (block.type === 'table') {
    return <div style={{ color:PAPER.faint, fontSize:12 }}>Table block — bind with mode=expand</div>;
  }
  return <div style={{ color:PAPER.faint, fontSize:12 }}>{block.type}</div>;
}

// ─── Timestamp column picker — supports composite (DATNR + HR + MIN + SEC) ──
// Stored as comma-separated string so it round-trips through the existing
// single-string `timestampColumn` field.
function TimestampColumnPicker({ value, onChange, columns, disabled }) {
  const selected = String(value || '').split(',').map(s => s.trim()).filter(Boolean);
  const available = (columns || []).filter(c => !selected.includes(c));

  const add = (c) => onChange([...selected, c].join(','));
  const remove = (c) => onChange(selected.filter(f => f !== c).join(','));
  const move = (c, dir) => {
    const i = selected.indexOf(c), j = i + dir;
    if (j < 0 || j >= selected.length) return;
    const next = selected.slice();
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next.join(','));
  };

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
      {selected.length > 0 && (
        <div style={{ display:'flex', flexWrap:'wrap', gap:4 }}>
          {selected.map((c, idx) => (
            <span key={c} style={{
              display:'inline-flex', alignItems:'center', gap:3,
              padding:'3px 3px 3px 8px', borderRadius:5,
              background:T.accentSoft, color:T.accent2,
              border:`1px solid ${T.line}`, fontSize:11
            }}>
              <span style={{ fontSize:9, color:T.faint, marginRight:2 }}>{idx + 1}.</span>
              {c}
              {selected.length > 1 && (
                <>
                  <button type="button" onClick={() => move(c, -1)} disabled={idx === 0}
                    style={{ background:'transparent', border:'none', color:T.faint, cursor: idx === 0 ? 'default' : 'pointer', padding:1, opacity: idx === 0 ? 0.3 : 1 }}
                    title="Move up">▲</button>
                  <button type="button" onClick={() => move(c, 1)} disabled={idx === selected.length - 1}
                    style={{ background:'transparent', border:'none', color:T.faint, cursor: idx === selected.length - 1 ? 'default' : 'pointer', padding:1, opacity: idx === selected.length - 1 ? 0.3 : 1 }}
                    title="Move down">▼</button>
                </>
              )}
              <button type="button" onClick={() => remove(c)}
                style={{ background:'transparent', border:'none', color:T.faint, cursor:'pointer', padding:'0 4px' }}
                title="Remove"><X size={10} /></button>
            </span>
          ))}
        </div>
      )}
      <select
        value=""
        disabled={disabled || available.length === 0}
        onChange={(e) => { if (e.target.value) add(e.target.value); }}
        style={inp()}
      >
        <option value="">
          {disabled ? 'pick a table first'
            : selected.length === 0 ? '— pick the timestamp column —'
            : available.length === 0 ? 'all columns added'
            : '+ add another column'}
        </option>
        {available.map(c => <option key={c} value={c}>{c}</option>)}
      </select>
    </div>
  );
}

// ─── Plant + MySQL data source + database for a Custom SQL binding ─────────
// The plant decides which registered connections the binding may read; the
// server re-checks the pair whenever the binding runs.
export function SqlMysqlTarget({ src, defaultPlant, patch }) {
  const plantId = src.plantId || defaultPlant || '';
  const [instances, setInstances] = useState([]);
  const [databases, setDatabases] = useState([]);

  useEffect(() => {
    if (!src.plantId && defaultPlant) patch({ plantId: defaultPlant });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!plantId) { setInstances([]); return; }
    let cancelled = false;
    mysqlService.listInstances(plantId)
      .then(list => {
        if (cancelled) return;
        setInstances(list);
        if (!src.mysqlInstanceId && list.length === 1) patch({ mysqlInstanceId: list[0].instanceId });
      })
      .catch(() => { if (!cancelled) setInstances([]); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plantId]);

  useEffect(() => {
    if (!src.mysqlInstanceId) { setDatabases([]); return; }
    mysqlService.getDatabases({ instanceId: src.mysqlInstanceId, plantId })
      .then(rows => setDatabases((rows || []).map(r => Object.values(r)[0]).filter(Boolean)))
      .catch(() => setDatabases([]));
  }, [src.mysqlInstanceId, plantId]);

  return (
    <>
      <Field label="Plant">
        <input style={inp()} value={src.plantId || defaultPlant || ''}
          onChange={e => patch({ plantId: e.target.value, mysqlInstanceId: '', mysqlDatabase: '' })} placeholder="e.g. smfa" />
      </Field>
      <Field label="MySQL data source">
        <select style={inp()} data-testid="sql-binding-mysql-instance" value={src.mysqlInstanceId || ''}
          onChange={e => patch({ mysqlInstanceId: e.target.value, mysqlDatabase: '' })}>
          <option value="">— select data source —</option>
          {instances.map(inst => (
            <option key={inst.instanceId} value={inst.instanceId}>{inst.label || inst.instanceId}{inst.isReadReplica ? ' (read replica)' : ''}</option>
          ))}
          {src.mysqlInstanceId && !instances.some(i => i.instanceId === src.mysqlInstanceId) && (
            <option value={src.mysqlInstanceId}>{src.mysqlInstanceId} (unavailable)</option>
          )}
        </select>
      </Field>
      <Field label="MySQL database (optional if your SQL names it)">
        <select style={inp()} value={src.mysqlDatabase || ''} disabled={!src.mysqlInstanceId}
          onChange={e => patch({ mysqlDatabase: e.target.value })}>
          <option value="">— none —</option>
          {databases.map(d => <option key={d} value={d}>{d}</option>)}
        </select>
      </Field>
    </>
  );
}

// ───────── BindingPanel — shared by both editors ─────────────────────────
// ─── Tag source editor — dynamic db / table / column dropdowns + filter UI ───
// Named exports for tests.
export function TagSourceEditor({ binding, src, set, defaultPlant, onChange }) {
  // Apply a partial patch to source.* in a SINGLE onChange call. Chaining
  // multiple `set()` calls overwrites earlier ones because each reads the
  // stale binding from closure — so cascading "change DB → clear table/col"
  // must happen in one go.
  const patchSource = (patch) => {
    onChange({ ...binding, source: { ...(binding.source || {}), ...patch } });
  };
  const engine = src.dbEngine;        // '' | 'questdb' | 'mysql' — explicit pick
  const isMySQL = engine === 'mysql';
  const isQuest = engine === 'questdb';
  const plantId = src.plantId || defaultPlant || '';

  const [databases, setDatabases] = useState([]);
  const [instances, setInstances] = useState([]);   // the plant's MySQL data sources
  const [tables,    setTables]    = useState([]);
  const [columns,   setColumns]   = useState([]);
  const [loading,   setLoading]   = useState({ db:false, tbl:false, col:false });

  // Default plantId on first render (both engines: a MySQL binding's plant
  // decides which data sources it may read)
  useEffect(() => {
    if (engine && !src.plantId && defaultPlant) set('source.plantId', defaultPlant);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [engine]);

  // Which MySQL data source this binding reads, under which plant
  const mysqlTarget = { instanceId: src.mysqlInstanceId, plantId };

  // The plant's MySQL data sources; with exactly one it is picked automatically
  useEffect(() => {
    if (!isMySQL || !plantId) { setInstances([]); return; }
    let cancelled = false;
    mysqlService.listInstances(plantId)
      .then(list => {
        if (cancelled) return;
        setInstances(list);
        if (!src.mysqlInstanceId && list.length === 1) patchSource({ mysqlInstanceId: list[0].instanceId });
      })
      .catch(() => { if (!cancelled) setInstances([]); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMySQL, plantId]);

  // Normalise rows-of-objects → flat string list (mysqlService.getDatabases
  // returns [{Database:'x'}, …] etc. We take the first column's value.)
  const toStringList = useCallback((rows) => {
    if (!Array.isArray(rows)) return [];
    return rows.map(r => {
      if (r == null) return '';
      if (typeof r === 'string') return r;
      const vals = Object.values(r);
      return vals.length ? String(vals[0]) : '';
    }).filter(Boolean);
  }, []);

  // Load DBs (MySQL) — runs only when engine is explicitly mysql
  useEffect(() => {
    if (!isMySQL) return;
    if (!src.mysqlInstanceId) { setDatabases([]); return; }
    setLoading(l => ({ ...l, db:true }));
    mysqlService.getDatabases(mysqlTarget)
      .then(d => setDatabases(toStringList(d)))
      .catch(() => setDatabases([]))
      .finally(() => setLoading(l => ({ ...l, db:false })));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMySQL, src.mysqlInstanceId, plantId, toStringList]);

  // Load tables — ONLY when an engine is explicitly picked AND prerequisites met
  useEffect(() => {
    if (!engine) { setTables([]); return; }
    if (isMySQL && !src.mysqlDatabase) { setTables([]); return; }
    if (isQuest && !plantId) { setTables([]); return; }
    setLoading(l => ({ ...l, tbl:true }));
    const loader = isMySQL
      ? mysqlService.getTables(src.mysqlDatabase, mysqlTarget)
      : questdbService.getTables(plantId);
    loader
      .then(t => setTables(toStringList(t)))
      .catch(() => setTables([]))
      .finally(() => setLoading(l => ({ ...l, tbl:false })));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [engine, isMySQL, isQuest, src.mysqlInstanceId, src.mysqlDatabase, plantId, toStringList]);

  // Load columns — same guard
  useEffect(() => {
    if (!engine || !src.table) { setColumns([]); return; }
    if (isMySQL && !src.mysqlDatabase) { setColumns([]); return; }
    if (isQuest && !plantId) { setColumns([]); return; }
    setLoading(l => ({ ...l, col:true }));
    reportService.getColumns(plantId || 'mysql', src.table, {
      dbEngine: engine,
      mysqlDatabase: src.mysqlDatabase,
      mysqlInstanceId: src.mysqlInstanceId
    })
      .then(cols => setColumns((cols || []).map(c => c.name || c)))
      .catch(() => setColumns([]))
      .finally(() => setLoading(l => ({ ...l, col:false })));
  }, [engine, isMySQL, isQuest, src.mysqlInstanceId, src.mysqlDatabase, plantId, src.table]);

  // Filter row helpers
  const filters = Array.isArray(src.filters) ? src.filters : [];
  const updateFilter = (idx, patch) => {
    const next = filters.map((f, i) => i === idx ? { ...f, ...patch } : f);
    set('source.filters', next);
  };
  const removeFilter = (idx) => set('source.filters', filters.filter((_, i) => i !== idx));
  const addFilter = () => set('source.filters', [...filters, { field:'', op:'=', value:'' }]);

  const valueDisabled = (op) => op === 'IS NULL' || op === 'IS NOT NULL';

  return (
    <>
      <Field label="Database">
        <SegControl
          value={src.dbEngine || ''}
          onChange={(v) => patchSource({ dbEngine: v, table: '', column: '', timestampColumn: '', mysqlDatabase: '', mysqlInstanceId: '', filters: [] })}
          options={[
            { v:'questdb', label:'QuestDB (live)' },
            { v:'mysql',   label:'MySQL / MariaDB' }
          ]}
        />
        {!engine && (
          <Hint>Pick where the value comes from — neither database is queried until you choose.</Hint>
        )}
      </Field>

      {engine && (
        <Field label="Plant">
          <input style={inp()} value={src.plantId || defaultPlant || ''}
            onChange={e => set('source.plantId', e.target.value)} placeholder="e.g. plantA" />
        </Field>
      )}

      {isMySQL && (
        <Field label="MySQL data source">
          <select style={inp()} data-testid="binding-mysql-instance" value={src.mysqlInstanceId || ''}
            onChange={(e) => patchSource({ mysqlInstanceId: e.target.value, mysqlDatabase: '', table: '', column: '', timestampColumn: '', filters: [] })}>
            <option value="">— select data source —</option>
            {instances.map(inst => (
              <option key={inst.instanceId} value={inst.instanceId}>{inst.label || inst.instanceId}{inst.isReadReplica ? ' (read replica)' : ''}</option>
            ))}
            {src.mysqlInstanceId && !instances.some(i => i.instanceId === src.mysqlInstanceId) && (
              <option value={src.mysqlInstanceId}>{src.mysqlInstanceId} (unavailable)</option>
            )}
          </select>
          {!plantId && <Hint>Set the plant first: it decides which data sources this binding may read.</Hint>}
        </Field>
      )}

      {isMySQL && (
        <Field label={loading.db ? 'MySQL database (loading…)' : 'MySQL database'}>
          <select style={inp()} value={src.mysqlDatabase || ''} disabled={!src.mysqlInstanceId}
            onChange={(e) => patchSource({ mysqlDatabase: e.target.value, table: '', column: '', timestampColumn: '', filters: [] })}>
            <option value="">— select database —</option>
            {databases.map(d => <option key={d} value={d}>{d}</option>)}
          </select>
        </Field>
      )}

      <Field label={loading.tbl ? 'Table (loading…)' : 'Table'}>
        <select style={inp()} value={src.table || ''} disabled={loading.tbl || tables.length === 0}
          onChange={(e) => patchSource({ table: e.target.value, column: '', timestampColumn: '', filters: [] })}>
          <option value="">{tables.length ? '— select table —' : 'no tables (pick db/plant first)'}</option>
          {tables.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
      </Field>

      <Field label={loading.col ? 'Value column (loading…)' : 'Value column (the actual reading)'}>
        <select style={inp()} value={src.column || ''} disabled={loading.col || columns.length === 0}
          onChange={(e) => set('source.column', e.target.value)}>
          <option value="">{columns.length ? '— select column —' : 'no columns (pick table first)'}</option>
          {columns.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
      </Field>

      <Field label="Timestamp column(s)">
        <Hint>
          For one timestamp column, pick that one. For tables that split it (e.g. DATNR + HR + MIN + SEC),
          add them in priority order — the engine orders by all of them DESC to find the latest row.
          Leave blank to use the default <code>timestamp</code>.
        </Hint>
        <TimestampColumnPicker
          value={src.timestampColumn || ''}
          onChange={(v) => set('source.timestampColumn', v)}
          columns={columns}
          disabled={!src.table || loading.col}
        />
      </Field>

      {/* ── WHERE filter builder ───────────────────────────────────── */}
      <Field label={`Filters${filters.length ? ` (${filters.length})` : ''}`}>
        <Hint>
          Restrict which rows count toward this binding. All filters are combined with <strong>AND</strong>.
          Need OR or nested groups? Switch the source to <em>custom query</em>.
        </Hint>
        {filters.length === 0 && (
          <div style={{
            padding:'10px 12px', fontSize:12, color:T.faint, fontStyle:'italic',
            background:T.bg, borderRadius:6, marginTop:6
          }}>No filters — every row in the table is considered.</div>
        )}
        {filters.map((f, i) => (
          <div key={i} style={{
            marginTop:8, padding:8, borderRadius:6,
            background:T.bg, border:`1px solid ${T.line}`,
            display:'flex', flexDirection:'column', gap:6
          }}>
            {/* row 1: field (wide) + remove */}
            <div style={{ display:'flex', gap:6, alignItems:'center' }}>
              <select style={{ ...inp(), padding:'6px 8px', fontSize:12, flex:1, minWidth:0 }}
                value={f.field || ''} onChange={(e) => updateFilter(i, { field: e.target.value })}>
                <option value="">field…</option>
                {columns.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
              <button onClick={() => removeFilter(i)}
                style={{ ...s.btn, ...s.btnGhost, padding:'5px 7px', color:T.danger }}
                title="Remove filter"><Trash2 size={12} /></button>
            </div>
            {/* row 2: op + value */}
            <div style={{ display:'flex', gap:6, alignItems:'center' }}>
              <select style={{ ...inp(), padding:'6px 8px', fontSize:12, width:90, flexShrink:0 }}
                value={f.op || '='} onChange={(e) => updateFilter(i, { op: e.target.value })}>
                <option value="=">=</option>
                <option value="!=">≠</option>
                <option value=">">&gt;</option>
                <option value="<">&lt;</option>
                <option value=">=">≥</option>
                <option value="<=">≤</option>
                <option value="LIKE">LIKE</option>
                <option value="IN">IN</option>
                <option value="NOT IN">NOT IN</option>
                <option value="BETWEEN">BETWEEN</option>
                <option value="IS NULL">is null</option>
                <option value="IS NOT NULL">not null</option>
              </select>
              <input style={{ ...inp(), padding:'6px 8px', fontSize:12, flex:1, minWidth:0 }}
                placeholder={
                  valueDisabled(f.op) ? '—' :
                  (f.op === 'IN' || f.op === 'NOT IN') ? 'a, b, c' :
                  f.op === 'BETWEEN' ? 'low, high' : 'value'
                }
                disabled={valueDisabled(f.op)}
                value={valueDisabled(f.op) ? '' : (f.value ?? '')}
                onChange={(e) => updateFilter(i, { value: e.target.value })}
              />
            </div>
          </div>
        ))}
        <button
          onClick={addFilter}
          style={{ ...s.btn, marginTop:8, padding:'6px 10px', fontSize:12 }}
          disabled={!src.table}
          title={!src.table ? 'Pick a table first' : ''}
        ><Plus size={12} /> Add filter</button>
      </Field>

      <Field label="Tag path (optional — for your own reference)">
        <input style={inp()} value={src.tag || ''} onChange={e => set('source.tag', e.target.value)}
          placeholder="uns://plantA/line1/pump42/vibration" />
      </Field>
    </>
  );
}

// ─── iOS-style toggle pill (visual only — wrapping element handles click) ─
function PillToggle({ checked }) {
  return (
    <span style={{
      width:30, height:17, borderRadius:999,
      background: checked ? T.accent : T.line2,
      position:'relative', flexShrink:0, transition:'background 0.15s'
    }}>
      <span style={{
        position:'absolute', top:2, left: checked ? 15 : 2,
        width:13, height:13, borderRadius:'50%',
        background:'#fff', transition:'left 0.15s',
        boxShadow:'0 1px 3px rgba(0,0,0,0.3)'
      }} />
    </span>
  );
}

// ─── Segmented control: fills the container, segments share space evenly ─
function SegControl({ value, onChange, options }) {
  return (
    <div style={{
      display:'flex', width:'100%', padding:3, borderRadius:8,
      background:T.bg, border:`1px solid ${T.line}`, gap:2, boxSizing:'border-box'
    }}>
      {options.map(({ v, label }) => {
        const active = value === v;
        return (
          <button
            key={v}
            onClick={() => onChange(v)}
            style={{
              flex:1, minWidth:0,
              padding:'6px 10px', fontSize:12, fontWeight: active ? 600 : 500,
              background: active ? T.accent : 'transparent',
              color: active ? '#fff' : T.mute,
              border:'none', borderRadius:6, cursor:'pointer', outline:'none',
              transition:'all 0.15s',
              whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis'
            }}
          >{label}</button>
        );
      })}
    </div>
  );
}

function BindingPanel({ binding, onChange, defaultPlant, embedded = false }) {
  const set = (path, value) => {
    const next = JSON.parse(JSON.stringify(binding));
    let cur = next; const parts = path.split('.');
    for (let i=0; i<parts.length-1; i++) { cur[parts[i]] = cur[parts[i]] || {}; cur = cur[parts[i]]; }
    cur[parts[parts.length-1]] = value;
    onChange(next);
  };

  const src = binding.source || {};
  const tr  = src.timeRule || {};
  const fmt = src.format || {};

  const needsWindow = tr.mode && !['snapshot'].includes(tr.mode);

  return (
    <div style={{ ...(embedded ? {} : { borderTop:`1px solid ${T.line}`, marginTop:14, paddingTop:14 }) }}>

      {/* ── Step 1 ───────────────────────────────────────── */}
      <Step n={1} title="Name this binding" />
      <Field label="Give it a short name so you can find it later">
        <input style={inp()} value={binding.label || ''} onChange={e => onChange({ ...binding, label:e.target.value })} placeholder="e.g. Pump 42 vibration at noon" />
      </Field>

      {/* Target summary + grow-forever toggle for single-cell bindings */}
      {(binding.target?.cell || binding.target?.range) && (
        <div style={{ marginBottom: 14, paddingBottom: 12, borderBottom: `1px solid ${T.line}` }}>
          <div style={{ display:'flex', alignItems:'center', gap:10, fontSize:12 }}>
            <span style={{
              padding:'3px 8px', borderRadius:5,
              background:T.accentSoft, color:T.accent2,
              fontFamily:'JetBrains Mono, monospace', fontWeight:600, fontSize:11
            }}>
              {binding.target?.range ? binding.target.range : binding.target?.cell}
            </span>
            <span style={{ color:T.faint }}>
              {binding.target?.range ? 'fixed range' : binding.target?.grow ? 'anchor — grows each run' : 'single cell'}
            </span>
          </div>
          {binding.target?.cell && !binding.target?.range && (
            <div style={{
              display:'flex', alignItems:'center', gap:10, marginTop:10,
              cursor:'pointer', userSelect:'none'
            }}
              onClick={() => onChange({ ...binding, target:{ ...binding.target, grow: !binding.target?.grow } })}
            >
              <PillToggle checked={!!binding.target?.grow} />
              <div style={{ flex:1 }}>
                <div style={{ fontSize:12, color:T.text, fontWeight:500 }}>Grow each run</div>
                <div style={{ fontSize:11, color:T.faint, marginTop:2, lineHeight:1.5 }}>
                  In append-to-same-file mode, each scheduled tick drops into the next cell below (or right of) <b>{binding.target.cell}</b>.
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Step 2 ───────────────────────────────────────── */}
      <Step n={2} title="Where should the value come from?" />
      <Field label="Pick one">
        <SegControl
          value={src.kind || 'tag'}
          onChange={(v) => set('source.kind', v)}
          options={[
            { v:'tag',    label:'Plant reading' },
            { v:'sql',    label:'Custom SQL' },
            { v:'static', label:'Static value' }
          ]}
        />
      </Field>

      {src.kind === 'tag' && (
        <TagSourceEditor binding={binding} src={src} set={set} defaultPlant={defaultPlant} onChange={onChange} />
      )}

      {src.kind === 'sql' && (
        <>
          <Field label="Database">
            <SegControl
              value={src.dbEngine || 'questdb'}
              onChange={(v) => onChange({ ...binding, source: { ...src, dbEngine: v, mysqlInstanceId: '', mysqlDatabase: '' } })}
              options={[
                { v:'questdb', label:'QuestDB (live)' },
                { v:'mysql',   label:'MySQL / MariaDB' }
              ]}
            />
          </Field>
          {src.dbEngine === 'mysql' && (
            <SqlMysqlTarget src={src} defaultPlant={defaultPlant}
              patch={(p) => onChange({ ...binding, source: { ...src, ...p } })} />
          )}
          <Field label="Your SQL query">
            <textarea style={{ ...inp(), height:120, fontFamily:'monospace', fontSize:12 }} value={src.sql || ''}
              onChange={e => set('source.sql', e.target.value)}
              placeholder={'SELECT v FROM table\nWHERE ts BETWEEN :windowStart AND :reportTime\nORDER BY ts DESC LIMIT 1'} />
            <Hint>
              You can use these placeholders in your query: <code>:reportTime</code> (when the report runs),
              <code>:windowStart</code> (start of the period you pick below), <code>:windowEnd</code> (end of that period).
            </Hint>
          </Field>
        </>
      )}

      {src.kind === 'static' && (
        <Field label="Type the value to put in the cell">
          <input style={inp()} value={src.value ?? ''} onChange={e => set('source.value', e.target.value)} placeholder="e.g. Plant A" />
        </Field>
      )}

      {src.kind !== 'static' && (
        <>
          {/* ── Step 3 ───────────────────────────────────────── */}
          <Step n={3} title="When should we look at the data?" />

          <Field label="Pick a reference moment">
            <select style={inp()} value={tr.anchor || 'report_time'} onChange={e => set('source.timeRule.anchor', e.target.value)}>
              <option value="report_time">The exact moment the report runs</option>
              <option value="custom_offset">A few minutes / hours before the report runs</option>
              <option value="explicit">A specific date and time I'll type in</option>
            </select>
          </Field>
          {tr.anchor === 'custom_offset' && (
            <Field label="How many seconds before the report runs?">
              <input type="number" style={inp()} value={Math.abs(tr.offsetSeconds || 0)} onChange={e => set('source.timeRule.offsetSeconds', -Math.abs(Number(e.target.value)))} placeholder="e.g. 3600 for 1 hour ago" />
              <Hint>Use seconds. Examples: <strong>60</strong> = 1 minute ago, <strong>3600</strong> = 1 hour ago, <strong>86400</strong> = 1 day ago.</Hint>
            </Field>
          )}
          {tr.anchor === 'explicit' && (
            <Field label="The exact date and time">
              <input style={inp()} value={tr.explicitTime || ''} onChange={e => set('source.timeRule.explicitTime', e.target.value)} placeholder="2026-05-21 11:00:00" />
              <Hint>Format: <code>YYYY-MM-DD HH:MM:SS</code></Hint>
            </Field>
          )}

          <Field label="Which value should we put in the cell?">
            <select style={inp()} value={tr.mode || 'snapshot'} onChange={e => set('source.timeRule.mode', e.target.value)}>
              <option value="snapshot">Just the reading at that moment</option>
              <option value="last">Most recent reading in the period</option>
              <option value="first">Oldest reading in the period</option>
              <option value="avg">Average over the period</option>
              <option value="min">Smallest reading in the period</option>
              <option value="max">Largest reading in the period</option>
              <option value="sum">Total of all readings in the period</option>
              <option value="count">How many readings happened</option>
              <option value="expand">Every reading (fills many cells)</option>
            </select>
            {tr.mode === 'snapshot' && (
              <Hint>Reads the single value at the moment you picked above. The period below is ignored.</Hint>
            )}
          </Field>

          {needsWindow && (
            <Field label="How far back should we look?">
              <input style={inp()} value={tr.window || ''} onChange={e => set('source.timeRule.window', e.target.value)} placeholder="e.g. 1h, 15m, 1d" />
              <Hint>
                Use <strong>s</strong> for seconds, <strong>m</strong> for minutes, <strong>h</strong> for hours, <strong>d</strong> for days.
                Examples: <code>30s</code>, <code>15m</code>, <code>1h</code>, <code>8h</code>, <code>1d</code>.
              </Hint>
            </Field>
          )}

          {tr.mode === 'expand' && (
            <>
              <Field label="Where should each reading go?">
                <select style={inp()} value={tr.expandInto || 'row'} onChange={e => set('source.timeRule.expandInto', e.target.value)}>
                  <option value="row">Down — one reading per row below the cell</option>
                  <option value="column">Across — one reading per column to the right</option>
                  <option value="sheet">Onto a new sheet</option>
                </select>
              </Field>
              <Field label="Group readings how often? (optional)">
                <input style={inp()} value={tr.interval || ''} onChange={e => set('source.timeRule.interval', e.target.value)} placeholder="e.g. 30s, 1m, 5m" />
                <Hint>
                  Leave this blank to write <strong>every single reading</strong> in the period.
                  Set a value like <code>30s</code> to group readings into 30-second buckets — one averaged value per cell.
                  Best when you want one reading per cell at a steady rhythm.
                </Hint>
              </Field>
            </>
          )}

          {/* ── Step 4 ───────────────────────────────────────── */}
          <Step n={4} title="How should it be shown in the cell?" />
          <Field label="Display the value as">
            <select style={inp()} value={fmt.kind || 'number'} onChange={e => set('source.format.kind', e.target.value)}>
              <option value="text">Plain text — show whatever comes back</option>
              <option value="number">Number — e.g. 42.50</option>
              <option value="percent">Percentage — e.g. 75.00%</option>
              <option value="currency">Currency — e.g. $42.50</option>
              <option value="date">Date only — e.g. 2026-05-21</option>
              <option value="datetime">Date and time — e.g. 2026-05-21 11:00:00</option>
              <option value="time">Time only — e.g. 11:00:00</option>
            </select>
          </Field>
          {['number','percent','currency'].includes(fmt.kind) && (
            <div style={{ display:'flex', gap:8 }}>
              <Field label="Decimal places" wide={false}>
                <input type="number" style={inp()} value={fmt.decimals ?? 2} onChange={e => set('source.format.decimals', Number(e.target.value))} />
              </Field>
              <Field label="Add before (e.g. $)" wide={false}>
                <input style={inp()} value={fmt.prefix || ''} onChange={e => set('source.format.prefix', e.target.value)} />
              </Field>
              <Field label="Add after (e.g. °C)" wide={false}>
                <input style={inp()} value={fmt.suffix || ''} onChange={e => set('source.format.suffix', e.target.value)} />
              </Field>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// Friendly numbered step header that replaces the old "SECTION" caps label
function Step({ n, title }) {
  return (
    <div style={{
      display:'flex', alignItems:'center', gap:8,
      marginTop:20, marginBottom:10,
      paddingBottom:8, borderBottom:`1px solid ${T.line}`
    }}>
      <span style={{
        fontSize:10, fontWeight:600, color:T.faint,
        letterSpacing:'0.06em'
      }}>{String(n).padStart(2,'0')}</span>
      <span style={{ fontSize:12.5, color:T.ink, fontWeight:600, letterSpacing:'-0.01em' }}>{title}</span>
    </div>
  );
}

// Small grey help text under a field
function Hint({ children }) {
  return (
    <div style={{ fontSize:11.5, color:T.faint, marginTop:5, lineHeight:1.5 }}>
      {children}
    </div>
  );
}

function BindingsList({ bindings, selectedId, previewData, onSelect, onRemove }) {
  if (!bindings.length) return null;
  const KIND_COLOR = { tag:'#a78bfa', sql:'#22d3ee', static:'#94a3b8' };
  return (
    <div style={{ display:'flex', flexDirection:'column', marginBottom:14 }}>
      {bindings.map(b => {
        const isSel = b.id === selectedId;
        const resolved = previewData?.[b.id];
        const kind = b.source?.kind || 'tag';
        const target = b.target?.cell
          ? (b.target?.grow ? `${b.target.cell} ↓` : b.target.cell)
          : b.target?.range || (b.target?.blockId ? 'canvas' : 'unbound');
        return (
          <div key={b.id}
            onClick={() => onSelect(b.id)}
            onMouseEnter={(e) => { if (!isSel) e.currentTarget.style.background = 'rgba(255,255,255,0.025)'; }}
            onMouseLeave={(e) => { if (!isSel) e.currentTarget.style.background = 'transparent'; }}
            style={{
              display:'flex', alignItems:'center', gap:10,
              padding:'8px 10px',
              cursor:'pointer',
              background: isSel ? T.accentSoft : 'transparent',
              borderLeft: `2px solid ${isSel ? T.accent2 : 'transparent'}`,
              transition: 'background 0.15s'
            }}
          >
            {/* tiny dot showing source kind */}
            <span style={{
              width:6, height:6, borderRadius:'50%',
              background: KIND_COLOR[kind] || KIND_COLOR.tag, flexShrink:0
            }} />
            {/* label only — no redundant subtitle */}
            <div style={{
              flex:1, minWidth:0, fontSize:12,
              color: isSel ? T.ink : T.text,
              fontWeight: isSel ? 600 : 500,
              whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis'
            }}>{b.label || '(untitled)'}</div>
            {/* target chip (small monospace) */}
            <span style={{
              fontSize:10, fontFamily:'JetBrains Mono, ui-monospace, monospace',
              color: T.faint, flexShrink:0
            }}>{target}</span>
            {/* preview value or error */}
            {resolved?.kind === 'scalar' && resolved?.value !== undefined && (
              <span style={{ fontSize:11, color:T.accent2, fontWeight:600, flexShrink:0 }}>
                {String(resolved.value).slice(0, 12)}
              </span>
            )}
            {resolved?.error && (
              <span style={{ fontSize:11, color:T.danger, flexShrink:0 }} title={resolved.error}>⚠</span>
            )}
            <button onClick={e => { e.stopPropagation(); onRemove(b.id); }}
              style={{
                background:'transparent', border:'none', padding:'4px 6px',
                color:T.faint, cursor:'pointer', borderRadius:4, opacity:0.7
              }}
              onMouseEnter={(e) => { e.currentTarget.style.opacity = 1; e.currentTarget.style.color = T.danger; }}
              onMouseLeave={(e) => { e.currentTarget.style.opacity = 0.7; e.currentTarget.style.color = T.faint; }}
              title="Remove">
              <Trash2 size={11} />
            </button>
          </div>
        );
      })}
    </div>
  );
}

// ───────── schedule modal ─────────────────────────────────────────────────
function ScheduleTemplateModal({ template, onClose, onScheduled }) {
  // If the template has any ranged bindings, default to append mode — that's
  // the common case ("log a value every N minutes into the next cell").
  // Recommend append + frequent cadence when ANY binding will accumulate data:
  // either a fixed range, or a grow-each-run anchor.
  const hasRanges = (template.bindings || []).some(b => !!b.target?.range || !!b.target?.grow);
  const [name, setName] = useState(`${template.name} — Daily`);
  const [type, setType] = useState(hasRanges ? 'every_minute' : 'every_day');
  const [intervalMinutes, setIntervalMinutes] = useState(60);
  const [appendMode, setAppendMode] = useState(hasRanges);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [done, setDone] = useState(null);

  const submit = async () => {
    setBusy(true); setErr('');
    try {
      const schedule = type === 'custom' ? { type:'custom', intervalMinutes } : { type };
      const report = await reportTemplateService.schedule(template.id, { name, schedule, appendMode });
      setDone(report);
      onScheduled?.(report);
    } catch (e) {
      setErr(e.response?.data?.message || e.message || 'Failed to schedule');
      setBusy(false);
    }
  };

  if (done) {
    return (
      <Modal title="Scheduled" onClose={onClose}>
        <div style={{ display:'flex', alignItems:'center', gap:14, padding:'18px 6px 6px' }}>
          <div style={{ width:44, height:44, borderRadius:10, background:T.accentSoft, display:'flex', alignItems:'center', justifyContent:'center' }}>
            <Check size={22} color={T.accent2} />
          </div>
          <div>
            <div style={{ fontSize:15, fontWeight:600, color:T.ink, marginBottom:4 }}>{done.name}</div>
            <div style={{ fontSize:13, color:T.mute }}>
              {scheduleLabel(done.schedule)} · First report is generating now — check <strong style={{ color:T.text }}>Generated Files</strong> in a moment.
            </div>
          </div>
        </div>
        <div style={{ display:'flex', justifyContent:'flex-end', marginTop:18 }}>
          <button style={{ ...s.btn, ...s.btnPrimary }} onClick={onClose}>Done</button>
        </div>
      </Modal>
    );
  }

  return (
    <Modal title="Schedule this template" onClose={onClose}>
      <Field label="Schedule name">
        <input style={inp()} value={name} onChange={e => setName(e.target.value)} />
      </Field>
      <Field label="How often?">
        <select style={inp()} value={type} onChange={e => setType(e.target.value)}>
          <option value="every_minute">Every minute</option>
          <option value="every_hour">Every hour</option>
          <option value="every_day">Every day</option>
          <option value="custom">Every N minutes…</option>
        </select>
      </Field>
      {type === 'custom' && (
        <Field label="Interval (minutes)">
          <input type="number" min={1} style={inp()} value={intervalMinutes} onChange={e => setIntervalMinutes(Number(e.target.value))} />
        </Field>
      )}

      <Field label="What happens on each run?">
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8 }}>
          <button
            type="button"
            onClick={() => setAppendMode(false)}
            style={{
              ...modeOpt(!appendMode),
              textAlign:'left'
            }}>
            <div style={{ fontWeight:600, fontSize:13, color:T.ink, marginBottom:3 }}>Fresh report</div>
            <div style={{ fontSize:11.5, color:T.mute, lineHeight:1.4 }}>New file every run. Each download is a complete snapshot.</div>
          </button>
          <button
            type="button"
            onClick={() => setAppendMode(true)}
            style={{
              ...modeOpt(appendMode),
              textAlign:'left'
            }}>
            <div style={{ fontWeight:600, fontSize:13, color:T.ink, marginBottom:3 }}>
              Append to same file {hasRanges && <span style={{ fontSize:10, background:T.accentSoft, color:T.accent2, padding:'2px 5px', borderRadius:3, marginLeft:4, fontWeight:600, letterSpacing:'0.04em' }}>RECOMMENDED</span>}
            </div>
            <div style={{ fontSize:11.5, color:T.mute, lineHeight:1.4 }}>Each run writes into the next cell of your bindings' range. One file that grows over time.</div>
          </button>
        </div>
      </Field>

      <div style={{ padding:'10px 12px', background:T.bg, border:`1px solid ${T.line}`, borderRadius:8, fontSize:12, color:T.mute, display:'flex', gap:8, alignItems:'flex-start', marginBottom:12 }}>
        <Clock size={14} color={T.accent2} style={{ marginTop:1, flexShrink:0 }} />
        <span>
          The first report will run immediately — you don't have to wait for the next tick.
          {appendMode && ' Subsequent runs will advance the cursor in each range binding by one slot per run.'}
        </span>
      </div>
      {err && <ErrorBanner msg={err} onClose={() => setErr('')} />}
      <div style={{ display:'flex', justifyContent:'space-between', marginTop:18 }}>
        <button style={s.btn} onClick={onClose} disabled={busy}>Cancel</button>
        <button style={{ ...s.btn, ...s.btnPrimary, opacity: busy ? 0.6 : 1 }} onClick={submit} disabled={busy}>
          {busy ? 'Scheduling…' : 'Schedule'}
        </button>
      </div>
    </Modal>
  );
}

function scheduleLabel(sch) {
  if (!sch) return '';
  if (sch.type === 'every_minute') return 'Runs every minute';
  if (sch.type === 'every_hour') return 'Runs every hour';
  if (sch.type === 'every_day') return 'Runs every day';
  if (sch.type === 'custom') return `Runs every ${sch.intervalMinutes || 60} minutes`;
  return sch.type;
}

// ───────── shared UI primitives ───────────────────────────────────────────
function Modal({ title, onClose, children }) {
  return (
    <div onClick={onClose} style={{ position:'fixed', inset:0, background:'rgba(11,18,32,0.55)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000 }}>
      <div onClick={e => e.stopPropagation()} style={{ width:500, maxWidth:'92vw', background:T.surface, borderRadius:14, padding:22, maxHeight:'88vh', overflowY:'auto', boxShadow:'0 20px 50px rgba(11,18,32,0.3)' }}>
        <div style={{ display:'flex', alignItems:'center', marginBottom:18 }}>
          <h3 style={{ margin:0, color:T.ink, fontSize:17, fontWeight:700 }}>{title}</h3>
          <div style={{ flex:1 }} />
          <button style={{ ...s.btn, ...s.btnGhost, padding:6 }} onClick={onClose}><X size={16} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Field({ label, children, wide = true }) {
  return (
    <div style={{ marginBottom:10, flex: wide ? 1 : 'initial' }}>
      <label style={{ display:'block', fontSize:11.5, color:T.mute, fontWeight:500, marginBottom:5, letterSpacing:'0.01em' }}>{label}</label>
      {children}
    </div>
  );
}
function SectionLabel({ children }) {
  return <div style={{ fontSize:11, color:T.faint, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.1em', marginTop:14, marginBottom:8 }}>{children}</div>;
}
function inp() {
  return { width:'100%', padding:'9px 11px', border:`1px solid ${T.line}`, borderRadius:7, fontSize:13, color:T.text, fontFamily:'inherit', background:T.bg, outline:'none', boxSizing:'border-box' };
}
function modeOpt(active) {
  return {
    padding:'11px 13px',
    borderRadius:9,
    border: `1px solid ${active ? T.accent : T.line}`,
    background: active ? T.accentSoft : T.bg,
    cursor:'pointer',
    transition:'all .15s ease',
    fontFamily:'inherit',
    width:'100%'
  };
}
function ErrorBanner({ msg, onClose }) {
  return (
    <div style={{ display:'flex', alignItems:'center', gap:8, padding:'9px 12px', background:'rgba(239,68,68,0.1)', border:'1px solid rgba(239,68,68,0.3)', borderRadius:8, color:'#f87171', fontSize:13, marginBottom:12 }}>
      <AlertCircle size={14} />
      <span style={{ flex:1 }}>{msg}</span>
      {onClose && <button onClick={onClose} style={{ background:'transparent', border:'none', color:'#f87171', cursor:'pointer' }}><X size={14} /></button>}
    </div>
  );
}

function FileDropzone({ file, onPick }) {
  const inRef = useRef(null);
  return (
    <div
      onClick={() => inRef.current?.click()}
      onDragOver={e => { e.preventDefault(); e.currentTarget.style.borderColor = T.accent; }}
      onDragLeave={e => { e.currentTarget.style.borderColor = T.line2; }}
      onDrop={e => { e.preventDefault(); e.currentTarget.style.borderColor = T.line2; const f = e.dataTransfer.files?.[0]; if (f) onPick(f); }}
      style={{ padding:24, border:`2px dashed ${T.line2}`, borderRadius:10, textAlign:'center', cursor:'pointer', background:T.bg, transition:'border-color .2s' }}
    >
      <input ref={inRef} type="file" accept=".xlsx" hidden onChange={e => { const f = e.target.files?.[0]; if (f) onPick(f); }} />
      <Upload size={20} color={T.accent2} />
      <div style={{ marginTop:10, fontSize:13, color:T.ink, fontWeight:600 }}>
        {file ? file.name : 'Drop a .xlsx here, or click to pick'}
      </div>
      <div style={{ fontSize:11, color:T.faint, marginTop:4 }}>
        {file ? `${(file.size/1024).toFixed(0)} KB` : 'Excel templates only'}
      </div>
    </div>
  );
}

// ───────── helpers ────────────────────────────────────────────────────────
function makeBlankBinding({ label, target = {}, plantId = '' }) {
  return {
    id: uid(),
    label,
    target,
    source: {
      kind: 'tag',
      // dbEngine left blank — the panel won't fire any fetches until the user
      // explicitly picks QuestDB or MySQL. Avoids hammering an offline agent.
      dbEngine: '',
      plantId,
      table: '', column: '', tag: '',
      sql: '',
      value: null,
      filters: [],
      timestampColumn: '',
      timeRule: { anchor:'report_time', offsetSeconds:0, mode:'snapshot', window:'', expandInto:'row' },
      format:   { kind:'number', decimals:2, prefix:'', suffix:'', dateFormat:'YYYY-MM-DD HH:mm:ss' }
    }
  };
}

function uid() {
  return Math.random().toString(36).slice(2, 11) + Date.now().toString(36);
}

function idxToCol(n) {
  let s = '';
  while (n > 0) { const m = (n - 1) % 26; s = String.fromCharCode(65 + m) + s; n = Math.floor((n - 1) / 26); }
  return s;
}
function colToIdx(col) {
  let n = 0; for (const ch of col.toUpperCase()) n = n * 26 + (ch.charCodeAt(0) - 64); return n;
}
function parseAddr(addr) {
  const m = String(addr || '').match(/^([A-Z]+)(\d+)$/i);
  if (!m) return null;
  return { col: m[1].toUpperCase(), row: parseInt(m[2], 10), colIdx: colToIdx(m[1]) };
}

function timeAgo(d) {
  const sec = Math.floor((Date.now() - d.getTime()) / 1000);
  if (sec < 5) return 'just now';
  if (sec < 60) return `${sec}s ago`;
  if (sec < 3600) return `${Math.floor(sec/60)}m ago`;
  return d.toLocaleTimeString();
}
